import { get, isEmpty, map } from 'lodash';
import * as Yup from 'yup';

// Helper function to convert multilingual data to old format for backward compatibility
export const convertToBackendFormat = (multiLangData) => {
  // Use English as primary language (fallback)
  return {
    name: multiLangData.name?.en || multiLangData.name || '',
    description: multiLangData.description?.en || multiLangData.description || '',
    translations: {
      name: multiLangData.name,
      description: multiLangData.description,
    }
  };
};

// Helper function to convert legacy data to multilingual format
export const convertFromBackendFormat = (legacyData) => {
  // If already converted, return as-is
  if (legacyData.name && typeof legacyData.name === 'object' && legacyData.name.en !== undefined) {
    return legacyData;
  }
  
  if (legacyData.translations) {
    // Convert venue options to multilingual format
    // venueOptions is an array of objects with id and name properties
    const venueOptions = legacyData.venueOptions?.map((option, index) => {
      const translatedOption = legacyData.translations.venueOptions?.[index];
      
      return {
        id: option.id, // Preserve the actual ID from the backend
        name: translatedOption || { en: option.name }
      };
    }) || [];

    return {
      id: legacyData.id, // Preserve the ID for editing
      name: legacyData.translations.name || { en: legacyData.name },
      description: legacyData.translations.description || { en: legacyData.description },
      venueOptions: venueOptions,
      isOnline: legacyData.isOnline || false,
    };
  }
  
  // Legacy format without translations - handle both venueOptions and venueSubOptions
  const optionsArray = legacyData.venueOptions || legacyData.venueSubOptions || [];
  const venueOptions = optionsArray.map((option, index) => {
    // optionsArray is always objects with id and name properties
    return {
      id: option.id,
      name: { en: option.name || '' }
    };
  });

  return {
    id: legacyData.id, // Preserve the ID for editing
    name: { en: legacyData.name || '' },
    description: { en: legacyData.description || '' },
    venueOptions: venueOptions,
    isOnline: legacyData.isOnline || false,
  };
};

export const initialValues = (form) => {
  if (!form) {
    return {
      name: { en: '' },
      description: { en: '' },
      venueOptions: [],
      isOnline: false,
    };
  }

  return convertFromBackendFormat(form);
};

// Multilingual validation schema
const multiLangStringSchema = (t) => Yup.object().shape({
  en: Yup.string().required(t('formulary.validation.english_translation_required')),
  nl: Yup.string().optional(),
});

export const venueTypeValidation = (t) =>
  Yup.object().shape({
    name: multiLangStringSchema(t),
    description: multiLangStringSchema(t),
    venueOptions: Yup.array().of(
      Yup.object().shape({
        id: Yup.mixed().nullable().optional(),
        name: multiLangStringSchema(t).optional(),
      })
    ).optional(),
    isOnline: Yup.boolean().optional(),
  });

export const addVenueInitialValues = (data) => {
  let venueOptions = get(data, 'venueType.option', []);
  if (!isEmpty(venueOptions)) {
    venueOptions = map(venueOptions, (item) => item.id);
  }
  return {
    venueTypeId: data && data.venueType ? data.venueType.id : '',
    subVenueOptions: venueOptions,
    name: data && data.name ? data.name : '',
    country: data && data.country ? data.country : '',
    streetAddress: data && data.streetAddress ? data.streetAddress : '',
    streetAddress2: data && data.streetAddress2 ? data.streetAddress2 : '',
    suburb: data && data.suburb ? data.suburb : '',
    state: data && data.state ? data.state : '',
    postCode: data && data.postCode ? data.postCode : '',
    isOwned: data ? Boolean(data.owned) : false,
  };
};

export const addVenueValidation = (t, optional) =>
  Yup.object().shape({
    venueTypeId: Yup.string().required(t('formulary.validation.venue_type_required')),
    name: Yup.string().required(t('formulary.validation.venue_name_required')),
    country: optional
      ? Yup.string().optional()
      : Yup.string().required(t('formulary.validation.country_required')),
    streetAddress: optional
      ? Yup.string().optional()
      : Yup.string().required(t('formulary.validation.street_address_required')),
    streetAddress2: optional
      ? Yup.string().optional()
      : Yup.string().optional().nullable(),
    suburb: optional
      ? Yup.string().optional()
      : Yup.string().required(t('formulary.validation.suburb_required')),
    state: optional
      ? Yup.string().optional()
      : Yup.string().required(t('formulary.validation.state_required')),
    postCode: optional
      ? Yup.string().optional()
      : Yup.string().required(t('formulary.validation.postcode_required')),
    isOwned: Yup.boolean().optional(),
  });
